import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Alert,
  Link,
  LoadingButton
} from '@one-thd/sui-atomic-components';
import {
  string,
  shape
} from '@thd-nucleus/data-sources';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Row } from '@thd-olt-component-react/core-ui';
import { func, bool, objectOf, arrayOf } from 'prop-types';
import { CABINET_VISUALIZER_DIGITAL_DATA } from '../../../../constants';
import { CabinetShape } from '../WallDesigner/type';
import { ProductInfo } from './CabinetDrawerComponents/ProductInfo';
import { ProductTypeSelect } from './CabinetDrawerComponents/ProductTypeSelect';
import { SSKUSelector } from './CabinetDrawerComponents/SSKUSelector';
import { useSuperSkuItem } from './hooks';
import { useCabinetVisualizerStore, useCabinetVisualizerDigitalDataStore } from '../../../../store';
import { useDrawerStore } from '../../../../store/ui';
import { useDesignAddTouchCabinet, useCartAddAccessories } from '../hooks/cabinetVisualizerHooks';
import {
  selectDesignAnchorSide,
  DEFAULT_WALLSIZE,
} from '../../../../store/cabinetVisualizer/selectors';
import {
  createBoundingBoxFromTouchpoint,
  isCornerCabinetByItemId
} from '../utils';
import {
  sendQuickViewOpenlAnalytics
} from '../../../../pages/cabinetVisualizer/analytics/analyticsFunctions';
import { CornerCabinetInfoMessage } from '../../CabinetSummary/CornerCabinetInfoMessage';
import { OutOfStockAlert } from '../../../OutOfStockAlert';

export const CabinetDrawer = React.memo(({
  designId,
  open,
  onClose,
  touchpoint,
  handleQuickView,
  editMode,
  editCabinet,
  accessory,
  isEditCabinetValid,
  onDataReady, // Add callback for when data is ready
  onCornerCabinetChange, // Callback for corner cabinet changes
  cabinets
}) => {
  const { trackAddItemToWallEvent } = useCabinetVisualizerDigitalDataStore();
  const enableTouchPointsOnBothSides = true; // tryGetQueryParam('enableTouchPointsOnBothSides') === 'true';
  const {
    selectedItemId,
    selectedParentId,
    superSkuProducts,
    modalAssortment
  } = useCabinetVisualizerStore((state) => state.cabinetVisualizer.skuDrawer);
  const [sskuDataLoading, setSSKUDataLoading] = useState(true);
  const [productDataLoading, setProductDataLoading] = useState(true);
  const [isDataLoadingComplete, setIsDataLoadingComplete] = useState(false);
  const { cabinetVisualizer } = useCabinetVisualizerStore();
  const selectedTouchPoint = useDrawerStore((state) => state.selectedTouchPoint);

  // Get defaultStyleChoiceId for landing page scenarios
  const { defaultStyleChoiceId, isLandingPage, collectionChangeTriggered } = useCabinetVisualizerStore(
    (state) => state.cabinetVisualizer
  );

  // Memoize modalType
  const modalType = useMemo(() => {
    return touchpoint?.type?.map((item) => {
      if (['WallFiller', 'BaseFiller'].includes(item)) {
        return 'Filler';
      }
      return item;
    });
  }, [touchpoint?.type]);

  const superSku = useSuperSkuItem(selectedItemId, modalType);

  const addTouchPointCabinet = useDesignAddTouchCabinet(designId, cabinets);
  const addAccessorytoCart = useCartAddAccessories(designId);

  // Memoize modal data checks
  const hasRequiredModalData = useMemo(() => {
    return modalType && modalType.some((type) => modalAssortment[type]
    && modalAssortment[type].skus && modalAssortment[type].skus.length > 0);
  }, [modalType, modalAssortment]);

  const hasBasicConfigData = useMemo(() => {
    return cabinetVisualizer?.styleChoiceId && cabinetVisualizer?.choices && cabinetVisualizer?.choiceMetadata;
  }, [cabinetVisualizer?.styleChoiceId, cabinetVisualizer?.choices, cabinetVisualizer?.choiceMetadata]);

  // We need both modal data AND basic config data to show the drawer content
  const isDataReady = useMemo(() => {
    // If collection change is in progress, data is not ready
    if (collectionChangeTriggered) {
      return false;
    }
    return hasRequiredModalData && hasBasicConfigData;
  }, [collectionChangeTriggered, hasRequiredModalData, hasBasicConfigData, modalType, modalAssortment]);

  const {
    cornerCabinet,
    cornerCabinetType
  } = isCornerCabinetByItemId(String(selectedParentId), superSkuProducts);

  useEffect(() => {
    if (onCornerCabinetChange) {
      onCornerCabinetChange(selectedItemId, cornerCabinet, cornerCabinetType);
    }
  }, [selectedItemId, cornerCabinet, cornerCabinetType, onCornerCabinetChange]);

  // Track when modal assortment data becomes available after style change
  useEffect(() => {
    // Only depend on primitive values
    const hasModalData = hasRequiredModalData;
    const hasConfigData = hasBasicConfigData;
    const dataIsReady = hasModalData && hasConfigData;

    if (dataIsReady && !isDataLoadingComplete) {
      setIsDataLoadingComplete(true);
      if (open && onDataReady) {
        onDataReady();
      }
    } else if (!dataIsReady && isDataLoadingComplete) {
      setIsDataLoadingComplete(false);
    }
  }, [hasRequiredModalData, hasBasicConfigData, open, isDataLoadingComplete, onDataReady]);
  const anchorPosition = selectDesignAnchorSide(cabinetVisualizer, designId);

  const cabinetInfo = {
    width: superSku.selectedWidthNumber,
    id: uuidv4(),
    height: superSku.selectedHeightNumber,
    color: superSku.selectedColor,
    itemId: selectedItemId,
    parentId: selectedParentId,
    productType: touchpoint?.type?.[0] || 'Wall',
    direction: touchpoint?.direction || anchorPosition,
    cornerCabinetType,
    cornerCabinet
  };

  const boundingBox = createBoundingBoxFromTouchpoint(selectedTouchPoint || {}, {
    ...cabinetInfo,
    maxHeight: DEFAULT_WALLSIZE.Rows,
    maxWidth: DEFAULT_WALLSIZE.Columns,
    anchorPosition
  });

  const openQuickViewDrawer = useCallback((event, quickViewATC) => {
    event.preventDefault();
    event.stopPropagation();
    sendQuickViewOpenlAnalytics();
    handleQuickView({ itemId: selectedItemId, isOpen: true, quickViewATC });
  }, [selectedItemId, handleQuickView]);

  const isCabinetValidToAddOrUpdate = () => {
    return isEditCabinetValid(cabinetInfo);
  };

  let accesoryName = 'Cabinet';

  if (accessory) {
    accesoryName = accessory.name || 'Cabinet'; // Use accessory name or default
  } else if (touchpoint && Array.isArray(touchpoint.type)) {
    const lowercaseTypes = touchpoint.type.map(type => type.toLowerCase());
    if (lowercaseTypes.includes('wallfiller') || lowercaseTypes.includes('basefiller')
    || lowercaseTypes.includes('filler')) {
      accesoryName = 'Cabinet Fillers';
    }
  }

  const handleAddCabinet = useCallback((event) => {
    event.target.setAttribute('disabled', true);
    onClose(event);
    handleQuickView({ itemId: null, isOpen: false, quickViewATC: null });

    if (editMode) {
      editCabinet(cabinetInfo);
      return;
    }

    if (touchpoint && touchpoint.type && !['Panels', 'Moulding'].includes(touchpoint.type[0]) && !accessory) {
      addTouchPointCabinet({ cabinetInfo, boundingBox });
    } else {
      addAccessorytoCart({ itemId: selectedItemId, parentId: selectedParentId, qty: 1 });
    }
    trackAddItemToWallEvent({
      itemInfo: {
        quantity: 1,
        productInfo: { sku: String(selectedItemId) },
        price: { basePrice: String(superSku.product.pricing.value) }
      },
      eventType: CABINET_VISUALIZER_DIGITAL_DATA.ACTION.ADD_ITEM_TO_WALL
    });
  }, [onClose, handleQuickView, editMode, editCabinet, cabinetInfo, touchpoint,
    accessory, addTouchPointCabinet, boundingBox, addAccessorytoCart, selectedItemId, selectedParentId, superSku]);

  const productTypeSelectKey = useMemo(() => {
  // Create a key that changes when collection changes to force re-mount
    return `${cabinetVisualizer?.styleChoiceId || 'no-style'}-${modalType?.join('-') || 'no-modal'}`;
  }, [cabinetVisualizer?.styleChoiceId, modalType]);

  // Don't render drawer content until we have the necessary data
  if (open && !isDataReady) {
    return (
      <Drawer
        open={open}
        position="right"
        onClose={(event) => onClose(event)}
        DrawerRootProps={{ style: { transition: 'right 0.3s ease-in 0s' } }}
      >
        <DrawerHeader title="Loading Cabinet Options..." onClose={onClose} />
        <DrawerBody>
          <div className="sui-flex sui-flex-col sui-justify-center sui-items-center sui-h-32 sui-p-4">
            <div className="sui-text-center">
              <div className="sui-mb-2">Loading cabinet options for your selected collection...</div>
              <div className="sui-text-sm sui-text-gray-600">
                This may take a moment while we fetch the latest cabinet data.
              </div>
            </div>
          </div>
        </DrawerBody>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      position="right"
      onClose={(event) => onClose(event)}
      DrawerRootProps={{ style: { transition: 'right 0.3s ease-in 0s' } }}
    >
      <DrawerHeader
        title={`Choose your ${accesoryName}`}
        onClose={onClose}
      />
      <DrawerBody>
        <ErrorBoundary name="cabinet-drawer-eb">
          <Row className="isBound">
            <ProductInfo setProductDataLoading={setProductDataLoading} />
            <ProductTypeSelect key={productTypeSelectKey} modalTypes={modalType} />
            <SSKUSelector editMode={editMode} setSSKUDataLoading={setSSKUDataLoading} />
            {!isCabinetValidToAddOrUpdate() && open
            && (
              <div className="sui-flex sui-flex-col sui-w-full sui-pt-3">
                <Alert status="error">
                  This cabinet in this specified position conflicts with other cabinets.
                  Please choose a different cabinet or remove the obstructing cabinet.
                </Alert>
              </div>
            )}
            {cornerCabinet && open && (
              <CornerCabinetInfoMessage cabinetType={cornerCabinetType} itemId={selectedItemId} />
            )}
          </Row>
        </ErrorBoundary>
      </DrawerBody>
      <DrawerFooter>
        {!superSku?.productIsInStock ? <OutOfStockAlert /> : null}
        <div className="sui-flex sui-gap-2 sui-pb-2">
          <LoadingButton
            onClick={handleAddCabinet}
            disabled={!isCabinetValidToAddOrUpdate()}
            loading={sskuDataLoading || productDataLoading}
            fullWidth
          >
            {editMode ? `Update ${accesoryName}` : `Add ${accesoryName}`}
          </LoadingButton>
        </div>
        <div className="quick-view sui-flex sui-gap-2">
          <Button
            variant="secondary"
            fullWidth
            onClick={(event) => openQuickViewDrawer(event, (
              <Button
                onClick={handleAddCabinet}
                disabled={!isCabinetValidToAddOrUpdate()}
                fullWidth
              >
                {editMode ? `Update ${accesoryName}` : `Add ${accesoryName}`}
              </Button>
            ))}
          >
            Quick View
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
});

CabinetDrawer.propTypes = {
  designId: string.isRequired,
  open: bool.isRequired,
  onClose: func,
  handleQuickView: func,
  touchpoint: shape({}),
  editMode: objectOf(bool),
  editCabinet: func,
  accessory: shape({}),
  isEditCabinetValid: func,
  onDataReady: func,
  onCornerCabinetChange: func,
  cabinets: arrayOf(CabinetShape),
};

CabinetDrawer.defaultProps = {
  onClose: undefined,
  handleQuickView: undefined,
  touchpoint: null,
  editMode: false,
  editCabinet: undefined,
  accessory: null,
  isEditCabinetValid: () => true,
  onDataReady: undefined,
  onCornerCabinetChange: undefined,
  cabinets: []
};
